import React from 'react';
import {Button, IButtonProps} from '../../components/button';
import {CardContent} from '../../components/card';
import {Dropdown} from '../../components/dropdown';
import {Grid, GridColumn} from '../../components/grid';
import {IIndicatorProps, Indicator} from '../../components/indicator';
import {IProgressProps, Progress} from '../../components/progress';
import {ITableProps, ITableRowProps, Table} from '../../components/table';
import {IDataInfoProps} from '../data-info';
import {IProgressWithIndicatorProps} from '../progress-with-indicator/progress-with-indicator';
import {ProgressWithIndicator} from '../progress-with-indicator';
import {TDashboardExpanded} from '../../templates/dashboard/dashboard';
import {handleScreenshotCapture} from '../screenshotbutton/screenshot';
import {isMobileDevice} from '../helpers';
import {isToolTipButton} from '../button/button';

export interface ITemplateGeneralCardProps extends ITemplateGeneralCardHeaderProps {
    table: ITemplateGeneralTableProps;
}

export interface ITemplateGeneralCardHeaderProps {
    title: string;
    actions?: IButtonProps[];
    more?: IButtonProps;
    isExpanded?: boolean;
    setExpanded?: (expanded: boolean) => void;
    name?: TDashboardExpanded;
}

export interface ITemplateGeneralTableProps extends ITableProps {
    rows: ITemplateGeneralRowProps[]| IDataInfoProps;
}

export interface ITemplateGeneralRowProps extends ITableRowProps {
    title: string;
    score: IProgressWithIndicatorProps;
    scoreTooltip?: string | JSX.Element;
    change?: IIndicatorProps;
    changeTooltip?: string | JSX.Element;
    activity: IProgressProps;
    activityTooltip?: string | JSX.Element;
    titleTooltip?: string | JSX.Element;
    subItems?: ITemplateGeneralRowProps[];
}

const DashboardGeneralCard = (cardProps: ITemplateGeneralCardProps): JSX.Element => {

    const getTableRow: (templateRow: ITemplateGeneralRowProps) => ITableRowProps = (templateRow: ITemplateGeneralRowProps) => (
        {
            ...templateRow,
            cells: templateRow.change ? [
                {content: templateRow.title, tooltip: templateRow.titleTooltip, comparableValue: templateRow.title},
                {content: <ProgressWithIndicator {...templateRow.score} />, tooltip: templateRow.scoreTooltip, comparableValue: templateRow.score.progress.percent},
                {content: <Indicator {...templateRow.change} />, tooltip: templateRow.changeTooltip, comparableValue: templateRow.change.number},
                {content: <Progress {...templateRow.activity} />, tooltip: templateRow.activityTooltip, comparableValue: templateRow.activity.percent},
            ] : [
                {content: templateRow.title, tooltip: templateRow.titleTooltip, comparableValue: templateRow.title},
                {content: <ProgressWithIndicator {...templateRow.score} />, tooltip: templateRow.scoreTooltip, comparableValue: templateRow.score.progress.percent},
                {content: <Progress {...templateRow.activity} />, tooltip: templateRow.activityTooltip, comparableValue: templateRow.activity.percent},
            ],
            subRows: templateRow.subItems && templateRow.subItems.map((subRow: ITemplateGeneralRowProps) => (
                getTableRow(subRow)
            )),
        }
    );

    const tableProps: ITableProps = {
        ...cardProps.table,
        rows: Array.isArray(cardProps.table.rows) ? cardProps.table.rows.map((row: ITemplateGeneralRowProps) => (
            getTableRow(row)
        )) : cardProps.table.rows,
    };


    const renderActions: (actions: IButtonProps[]) => JSX.Element | JSX.Element[] = (actions: IButtonProps[]) => {
        if (actions.length === 1) {
            return <Button {...actions[0]} color="white" icon="info" style="link"/>;
        }

        return actions.map((action: IButtonProps, i: number) => {
            if (isToolTipButton(action)) {
                return (
                    <Dropdown className={'button-container'}
                        key={'button_tooltip' + i}
                        trigger={{
                            children:
                                      <Button {...action} style="link"/>,
                        }}>
                        {action.tooltip}
                    </Dropdown>
                );
            } else {
                return (<Button key={'history-action-' + i} {...action} style="link"/>);
            }
        });
    };

    const getCardHeader = (headerProps: ITemplateGeneralCardHeaderProps): JSX.Element => (
        <Grid noWrap={['sm']} align={['middle-xs']}>
            <GridColumn width={['xs-8', 'max-sm']}>
                <h3>{headerProps.title}</h3>
            </GridColumn>
            {(headerProps.actions || headerProps.more || headerProps.setExpanded) &&
                <GridColumn width={['xs-4', 'min-sm']}>
                    <Grid align={['middle-xs']} justify={['end-xs']}>
                        {headerProps.actions &&
                        <GridColumn width={['min-xs']} className={'button-container'}>
                            {renderActions(headerProps.actions)}
                        </GridColumn>
                        }
                        {headerProps.setExpanded &&
                            <GridColumn width={['xs']}>
                                {!isMobileDevice() &&
                                <Button
                                    icon={'print'} className={'button__noFill'} color={'white'}
                                    onClick={(): void => {

                                        if (headerProps.setExpanded) {
                                            headerProps.setExpanded(true);
                                            setTimeout(() => {
                                                handleScreenshotCapture(headerProps.name ?? undefined);
                                            }, 500);
                                        } else {
                                            handleScreenshotCapture(headerProps.name ?? undefined);
                                        }
                                    }}
                                />
                                }
                                <Button
                                    {...headerProps.more}
                                    text=""
                                    color="white"
                                    icon={headerProps.isExpanded ? 'collapse' : 'expand'}
                                    onClick={(event: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>): void => {
                                        event.preventDefault();

                                        if (headerProps.setExpanded) {
                                            headerProps.setExpanded(!headerProps.isExpanded);
                                        }

                                        if (headerProps.more && headerProps.more.onClick) {
                                            headerProps.more.onClick(event);
                                        }
                                    }}
                                    style="link"
                                />
                            </GridColumn>
                        }
                    </Grid>
                </GridColumn>
            }
        </Grid>
    );

    return (
        <CardContent>
            <div className="text text--double">
                {getCardHeader(cardProps)}
                <Table {...tableProps}
                    limitHeight={!cardProps.isExpanded} isExpanded={cardProps.isExpanded} />
            </div>
        </CardContent>
    );
};

export default DashboardGeneralCard;
